import { createApiError } from './apiError';
import { resolveApiBaseUrl } from './apiBase';

const API_URL = resolveApiBaseUrl();

const getAuthToken = (): string | null => {
  const token = localStorage.getItem('idToken') || localStorage.getItem('authToken');
  return token ? token.trim() : null;
};

export type PublicFormField = {
  name: string;
  label?: string;
  description?: string;
  type: string;
  required?: boolean;
  /** When false, omit blank choice; only explicit options (select / dropdown). */
  allowEmptyOption?: boolean;
  options?: any[];
  value?: any;
  fields?: string;
  table?: string;
  order?: number;
};

export type PublicFormResponse = {
  uid: string;
  componentUid: string;
  componentSlug: string;
  title: string;
  subtitle?: string;
  iconUrl?: string;
  description: string;
  /** Heading when closed; empty defaults to "Form closed". */
  closedHeader?: string;
  /** Shown when the form is closed; empty uses a default visitor message. */
  closedMessage?: string;
  successMessage?: string;
  nextButtonLabel?: string;
  requireLogin: boolean;
  singleSubmissionPerEmail: boolean;
  submissionPolicy?: 'open' | 'overwrite' | 'duplicate' | 'block';
  /** When set on edit-mode forms, only these field names may be changed on resubmit. Empty allows all. */
  editableFieldsOnUpdate?: string[];
  fields: PublicFormField[];
};

export type PublicFormFetchResult = {
  form: PublicFormResponse;
  /** True when the form is closed; `form.fields` is empty and submissions are not accepted. */
  closed: boolean;
};

export const uploadPublicFormFile = async (token: string, file: File): Promise<{ success: boolean; filename?: string; message?: string }> => {
  const url = API_URL ? `${API_URL}/public/forms/${token}/upload` : `/public/forms/${token}/upload`;
  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(url, {
    method: 'POST',
    body: formData
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || 'File upload failed');
  }
  if (!data.success) {
    throw new Error(data.message || 'File upload failed');
  }
  return { success: true, filename: data.filename };
};

export const fetchPublicForm = async (
  token: string,
  options?: { submissionUid?: string }
): Promise<PublicFormFetchResult> => {
  const authToken = getAuthToken();
  const qs =
    options?.submissionUid && String(options.submissionUid).trim()
      ? `?submissionUid=${encodeURIComponent(String(options.submissionUid).trim())}`
      : '';
  const path = `/public/forms/${token}${qs}`;
  const url = API_URL ? `${API_URL}${path}` : path;
  const headers: Record<string, string> = {};
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  const response = await fetch(url, { headers });
  const responseText = await response.text();

  if (!response.ok) {
    throw new Error(responseText || 'Failed to fetch public form');
  }

  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch public form');
  }

  return { form: data.form, closed: Boolean(data.closed) };
};

export type PublicFormListItem = {
  uid: string;
  title: string;
  description: string;
  publicToken: string;
};

export const fetchPublicFormsList = async (): Promise<PublicFormListItem[]> => {
  const url = API_URL ? `${API_URL}/public/forms` : `/public/forms`;
  const response = await fetch(url);
  const responseText = await response.text();

  if (!response.ok) {
    throw new Error(responseText || 'Failed to fetch public forms');
  }

  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch public forms');
  }

  return data.forms || [];
};

export type PublicFormBackupMeta = {
  clientSessionId?: string;
  unfinishedUid?: string | null;
};

export const sendPublicFormBackup = async (
  token: string,
  data: Record<string, any>,
  meta?: PublicFormBackupMeta
): Promise<{ unfinishedUid?: string }> => {
  const url = API_URL ? `${API_URL}/public/forms/${token}/backup` : `/public/forms/${token}/backup`;
  const body: Record<string, unknown> = { data };
  if (meta?.clientSessionId) {
    body.clientSessionId = meta.clientSessionId;
  }
  if (meta?.unfinishedUid) {
    body.unfinishedUid = meta.unfinishedUid;
  }
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    const parsed = await response.json().catch(() => ({}));
    if (!response.ok) {
      console.warn('Form backup request failed:', response.status);
      return {};
    }
    if (parsed && typeof parsed.unfinishedUid === 'string') {
      return { unfinishedUid: parsed.unfinishedUid };
    }
    return {};
  } catch (err) {
    console.warn('Form backup request error:', err);
    return {};
  }
};

export type PublicFormSubmitMeta = {
  publicFormDraftId?: string | null;
  unfinishedClientSessionId?: string | null;
  /** When set, overwrite/duplicate updates this submission instead of the most recent one. */
  submissionUid?: string | null;
};

export type PublicFormSubmitResult = {
  item?: any;
  message: string;
};

export const submitPublicForm = async (
  token: string,
  payload: Record<string, any>,
  meta?: PublicFormSubmitMeta
): Promise<PublicFormSubmitResult> => {
  const authToken = getAuthToken();
  const url = API_URL ? `${API_URL}/public/forms/${token}/submissions` : `/public/forms/${token}/submissions`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  const body: Record<string, unknown> = { ...payload };
  if (meta?.publicFormDraftId) {
    body._publicFormDraftId = meta.publicFormDraftId;
  }
  if (meta?.unfinishedClientSessionId) {
    body._unfinishedClientSessionId = meta.unfinishedClientSessionId;
  }
  const submissionUid =
    meta?.submissionUid != null ? String(meta.submissionUid).trim() : '';
  if (submissionUid) {
    body._submissionUid = submissionUid;
  }
  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });
  const responseText = await response.text();

  if (!response.ok) {
    const retryAfter = response.status === 429
      ? (parseInt(response.headers.get('Retry-After') || '60', 10) || 60)
      : undefined;
    let msg = responseText || 'Failed to submit public form';
    try {
      const parsed = responseText ? JSON.parse(responseText) : null;
      if (parsed && typeof parsed.message === 'string') msg = parsed.message;
    } catch {
      // use msg as-is
    }
    throw createApiError(msg, response.status, retryAfter);
  }

  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to submit public form');
  }

  return {
    item: data.item ?? data.data,
    message: typeof data.message === 'string' ? data.message : ''
  };
};

export const fetchPublicFormDraft = async (
  draftId: string
): Promise<{ token?: string; data?: Record<string, any> }> => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required');
  }
  const url = API_URL ? `${API_URL}/public/forms/drafts/${draftId}` : `/public/forms/drafts/${draftId}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  const responseText = await response.text();

  if (!response.ok) {
    throw new Error(responseText || 'Failed to load submission draft');
  }

  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to load submission draft');
  }

  return data.draft || {};
};

export type PublicFormDraftLog = {
  uid: string;
  email?: string;
  createdAt?: string;
  expiresAt?: string;
  fieldCount?: number;
};

export type PublicFormSubmissionLog = {
  uid: string;
  email?: string;
  createdAt?: string;
  fieldCount?: number;
  dataSource?: string;
};

export type FollowUpRequest = {
  uid: string;
  formUid: string;
  followUpFormUid: string;
  followUpFormTitle?: string;
  followUpToken?: string;
  followUpPath?: string;
  submissionId: string;
  submitterEmail: string;
  triggerField: string;
  triggerValue: string;
  completionField?: string;
  completionValue?: string;
  status: string;
  createdAt?: string;
  sentAt?: string;
  completedAt?: string;
  /** True when the linked form submission row is archived (_archived). */
  submissionArchived?: boolean;
  delivery?: {
    email?: boolean;
    inApp?: boolean;
  };
};

export type MyFormSubmissionLine = { label: string; value: string };

export type MyFormSubmissionEntry = {
  uid: string;
  formUid: string;
  formToken: string;
  archived?: boolean;
  header: string;
  /** Set when header is a collection field (not form title). */
  headerLabel?: string;
  lines?: MyFormSubmissionLine[];
  footer?: string;
  /** Legacy response fields (older API). */
  formTitle?: string;
  createdAt?: string;
};

function formatLegacySubmissionDate(createdAt: string | undefined): string {
  if (!createdAt) return '';
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function normalizeMyFormSubmissionEntry(raw: Record<string, unknown>): MyFormSubmissionEntry {
  const uid = typeof raw.uid === 'string' ? raw.uid : String(raw.uid ?? '');
  const formUid = typeof raw.formUid === 'string' ? raw.formUid : String(raw.formUid ?? '');
  const formToken = typeof raw.formToken === 'string' ? raw.formToken : String(raw.formToken ?? '');
  const archived = Boolean(raw.archived);
  const formTitle = typeof raw.formTitle === 'string' ? raw.formTitle : undefined;
  const createdAt = typeof raw.createdAt === 'string' ? raw.createdAt : undefined;

  if (typeof raw.header === 'string' && raw.header.trim() !== '') {
    const parsedLines = Array.isArray(raw.lines)
      ? raw.lines
        .filter(
          (line): line is MyFormSubmissionLine =>
            !!line
            && typeof line === 'object'
            && typeof (line as MyFormSubmissionLine).label === 'string'
            && typeof (line as MyFormSubmissionLine).value === 'string'
        )
        .map((line) => ({
          label: (line as MyFormSubmissionLine).label,
          value: (line as MyFormSubmissionLine).value
        }))
      : undefined;
    const footerFromApi = typeof raw.footer === 'string' && raw.footer.trim() !== ''
      ? raw.footer
      : undefined;
    const submittedLine = parsedLines?.find((line) => line.label === 'Submitted');
    const lines = parsedLines?.filter((line) => line.label !== 'Submitted');
    const footer = footerFromApi ?? submittedLine?.value;
    const headerLabel =
      typeof raw.headerLabel === 'string' && raw.headerLabel.trim() !== ''
        ? raw.headerLabel
        : undefined;
    return {
      uid,
      formUid,
      formToken,
      archived,
      header: raw.header,
      headerLabel,
      lines: lines && lines.length > 0 ? lines : undefined,
      footer,
      formTitle,
      createdAt
    };
  }

  const header = formTitle || 'Form';
  const dateLine = formatLegacySubmissionDate(createdAt);
  return {
    uid,
    formUid,
    formToken,
    archived,
    header,
    footer: dateLine || undefined,
    formTitle,
    createdAt
  };
}

export class ArchivedSubmissionError extends Error {
  readonly archived = true as const;
  constructor(message: string) {
    super(message);
    this.name = 'ArchivedSubmissionError';
  }
}

export type MyFormSubmissionDetailField = {
  name: string;
  label: string;
  type: string;
};

export type MyFormSubmissionDetail = {
  formTitle: string;
  fields: MyFormSubmissionDetailField[];
  data: Record<string, unknown>;
  /** Human-readable labels for relation fields (server-resolved); raw ids remain in data for editors. */
  relationDisplay?: Record<string, string>;
};

export const fetchPublicFormDraftsAdmin = async (
  token: string,
  limit: number = 50
): Promise<PublicFormDraftLog[]> => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required');
  }
  const query = limit ? `?limit=${encodeURIComponent(limit)}` : '';
  const url = API_URL
    ? `${API_URL}/public/forms/${token}/drafts${query}`
    : `/public/forms/${token}/drafts${query}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  const responseText = await response.text();

  if (!response.ok) {
    throw new Error(responseText || 'Failed to fetch drafts');
  }

  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch drafts');
  }

  return Array.isArray(data.drafts) ? data.drafts : [];
};

export const fetchPublicFormSubmissionsAdmin = async (
  token: string,
  limit: number = 50
): Promise<PublicFormSubmissionLog[]> => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required');
  }
  const query = limit ? `?limit=${encodeURIComponent(limit)}` : '';
  const url = API_URL
    ? `${API_URL}/public/forms/${token}/submissions${query}`
    : `/public/forms/${token}/submissions${query}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  const responseText = await response.text();

  if (!response.ok) {
    throw new Error(responseText || 'Failed to fetch submissions');
  }

  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch submissions');
  }

  return Array.isArray(data.submissions) ? data.submissions : [];
};

export const deletePublicFormDraftAdmin = async (token: string, draftId: string): Promise<void> => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required');
  }
  if (!token) {
    throw new Error('Form token is required');
  }
  if (!draftId) {
    throw new Error('Draft ID is required');
  }
  const url = API_URL
    ? `${API_URL}/public/forms/${token}/drafts/${draftId}`
    : `/public/forms/${token}/drafts/${draftId}`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(responseText || 'Failed to delete draft');
  }
  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to delete draft');
  }
};

export const deletePublicFormDraftsAdmin = async (token: string): Promise<void> => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required');
  }
  if (!token) {
    throw new Error('Form token is required');
  }
  const url = API_URL ? `${API_URL}/public/forms/${token}/drafts` : `/public/forms/${token}/drafts`;
  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(responseText || 'Failed to delete drafts');
  }
  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to delete drafts');
  }
};

export const fetchPublicRelationOptions = async (
  token: string,
  relatedSlug: string,
  options?: { limit?: number; submissionUid?: string }
): Promise<any[]> => {
  const authToken = getAuthToken();
  const limit = options?.limit ?? 200;
  const params = new URLSearchParams();
  if (limit) {
    params.set('limit', String(limit));
  }
  const submissionUid =
    options?.submissionUid != null ? String(options.submissionUid).trim() : '';
  if (submissionUid) {
    params.set('submissionUid', submissionUid);
  }
  const query = params.toString() ? `?${params.toString()}` : '';
  const url = API_URL
    ? `${API_URL}/public/forms/${token}/relations/${relatedSlug}${query}`
    : `/public/forms/${token}/relations/${relatedSlug}${query}`;
  const headers: Record<string, string> = {};
  if (authToken) {
    headers.Authorization = `Bearer ${authToken}`;
  }
  const response = await fetch(url, { headers });
  const responseText = await response.text();

  if (!response.ok) {
    throw new Error(responseText || 'Failed to fetch relation options');
  }

  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch relation options');
  }

  return Array.isArray(data.items) ? data.items : [];
};

export const fetchFollowUpRequests = async (): Promise<FollowUpRequest[]> => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required');
  }
  const url = API_URL ? `${API_URL}/public/forms/followups` : `/public/forms/followups`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(responseText || 'Failed to fetch follow-up requests');
  }
  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch follow-up requests');
  }
  return Array.isArray(data.followUps) ? data.followUps : [];
};

export const fetchMyFormSubmissions = async (limit?: number): Promise<MyFormSubmissionEntry[]> => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required');
  }
  const query = limit ? `?limit=${encodeURIComponent(limit)}` : '';
  const url = API_URL
    ? `${API_URL}/public/forms/my-submissions${query}`
    : `/public/forms/my-submissions${query}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(responseText || 'Failed to fetch form submissions');
  }
  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch form submissions');
  }
  return Array.isArray(data.submissions)
    ? data.submissions.map((item: Record<string, unknown>) => normalizeMyFormSubmissionEntry(item))
    : [];
};

export const fetchMyFormSubmissionDetail = async (
  formToken: string,
  uid: string
): Promise<MyFormSubmissionDetail> => {
  const authToken = getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required');
  }
  const url = API_URL
    ? `${API_URL}/public/forms/my-submissions/${encodeURIComponent(formToken)}/${encodeURIComponent(uid)}`
    : `/public/forms/my-submissions/${encodeURIComponent(formToken)}/${encodeURIComponent(uid)}`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${authToken}`
    }
  });
  const responseText = await response.text();
  if (!response.ok) {
    try {
      const errBody = JSON.parse(responseText) as { archived?: boolean; message?: string };
      if (response.status === 403 && errBody.archived) {
        throw new ArchivedSubmissionError(
          typeof errBody.message === 'string' ? errBody.message : 'This submission has been archived.'
        );
      }
    } catch (e) {
      if (e instanceof ArchivedSubmissionError) {
        throw e;
      }
    }
    throw new Error(responseText || 'Failed to fetch submission details');
  }
  const data = JSON.parse(responseText);
  if (!data.success) {
    throw new Error(data.message || 'Failed to fetch submission details');
  }
  const relationDisplay =
    data.relationDisplay && typeof data.relationDisplay === 'object' && !Array.isArray(data.relationDisplay)
      ? Object.fromEntries(
        Object.entries(data.relationDisplay as Record<string, unknown>).filter(
          ([k, v]) => typeof k === 'string' && typeof v === 'string' && v.trim() !== ''
        )
      )
      : undefined;

  return {
    formTitle: data.formTitle || 'Form',
    fields: Array.isArray(data.fields) ? data.fields : [],
    data: data.data && typeof data.data === 'object' ? data.data : {},
    ...(relationDisplay && Object.keys(relationDisplay).length > 0 ? { relationDisplay } : {})
  };
};
